/* Kripson Home motion layer (GSAP + ScrollTrigger, bundled locally).
   Principles: short, soft, once. Content is never hidden if this file fails:
   CSS failsafes reveal everything, and reduced-motion visitors get no animation. */
(() => {
  'use strict';
  const root = document.documentElement;
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const showAll = () => {
    root.classList.add('motion-ready');
    $$('[data-reveal]').forEach((el) => el.classList.add('is-in'));
  };

  if (!root.classList.contains('motion') || !window.gsap || !window.ScrollTrigger) { showAll(); return; }
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { showAll(); return; }

  const { gsap, ScrollTrigger } = window;
  gsap.registerPlugin(ScrollTrigger);
  gsap.defaults({ ease: 'power3.out', duration: 0.9 });

  /* Split a heading into words that rise from behind a mask. Keeps <em> etc. */
  const splitWords = (el) => {
    if (!el || el.dataset.split) return [];
    el.dataset.split = '1';
    el.setAttribute('aria-label', el.textContent.trim().replace(/\s+/g, ' '));
    const words = [];
    const walk = (node) => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === 3) {
          const frag = document.createDocumentFragment();
          child.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
            const outer = document.createElement('span');
            outer.className = 'split-w';
            outer.setAttribute('aria-hidden', 'true');
            const inner = document.createElement('span');
            inner.textContent = part;
            outer.appendChild(inner);
            frag.appendChild(outer);
            words.push(inner);
          });
          child.replaceWith(frag);
        } else if (child.nodeType === 1) walk(child);
      });
    };
    walk(el);
    return words;
  };

  /* ---------- 1. Set initial states (same frame, so nothing flashes) ---------- */
  const reveals = $$('[data-reveal]');
  gsap.set(reveals, { autoAlpha: 0, y: 28 });
  reveals.forEach((el) => el.classList.add('is-in'));

  const hero = $('.hero');
  const heroCopy = hero ? $$('.hero__copy > *', hero).filter((el) => el.tagName !== 'H1') : [];
  const heroTitle = hero ? $('h1', hero) : null;
  const heroWords = splitWords(heroTitle);
  const stackImgs = hero ? $$('.stack__img', hero) : [];
  const stackTags = hero ? $$('.stack__tag', hero) : [];

  const pageHead = $('.page-head, .coll-hero, .pinfo');
  const pageTitle = pageHead ? $('h1', pageHead) : null;
  const pageWords = hero ? [] : splitWords(pageTitle);
  const pageBits = pageHead && !hero ? Array.from(pageHead.children).filter((el) => el !== pageTitle) : [];

  if (heroWords.length) gsap.set(heroWords, { yPercent: 110 });
  gsap.set(heroCopy, { autoAlpha: 0, y: 22 });
  gsap.set(stackImgs, { clipPath: 'inset(100% 0% 0% 0% round 22px)' });
  gsap.set(stackImgs.map((s) => $('img', s)).filter(Boolean), { scale: 1.12 });
  gsap.set(stackTags, { autoAlpha: 0, scale: 0.85, y: 8 });
  if (pageWords.length) gsap.set(pageWords, { yPercent: 110 });
  gsap.set(pageBits, { autoAlpha: 0, y: 18 });

  root.classList.add('motion-ready'); // GSAP now owns the first screen; CSS pre-hide switches off

  /* ---------- 2. First-screen entrance, after the preloader lifts ---------- */
  const intro = () => {
    const tl = gsap.timeline({ defaults: { ease: 'power4.out' } });
    if (hero) {
      tl.to(heroWords, { yPercent: 0, duration: 1.05, stagger: 0.055 }, 0)
        .to(heroCopy, { autoAlpha: 1, y: 0, duration: 0.8, stagger: 0.08 }, 0.25)
        .to(stackImgs, { clipPath: 'inset(0% 0% 0% 0% round 22px)', duration: 1.2, stagger: 0.14, ease: 'expo.out' }, 0.1)
        .to(stackImgs.map((s) => $('img', s)).filter(Boolean), { scale: 1, duration: 1.6, stagger: 0.14, ease: 'expo.out' }, 0.1)
        .to(stackTags, { autoAlpha: 1, scale: 1, y: 0, duration: 0.6, stagger: 0.12, ease: 'back.out(1.8)' }, 0.85);
    }
    if (pageWords.length || pageBits.length) {
      tl.to(pageWords, { yPercent: 0, duration: 0.95, stagger: 0.045 }, 0)
        .to(pageBits, { autoAlpha: 1, y: 0, duration: 0.7, stagger: 0.07 }, 0.15);
    }
    // Gentle float on the hero tags once they're in
    stackTags.forEach((t, i) => gsap.to(t, { y: i % 2 ? 5 : -5, duration: 2.6 + i * 0.4, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: 1.6 }));
  };
  (window.KH && window.KH.ready ? window.KH.ready : Promise.resolve()).then(intro);

  /* ---------- 3. Scroll reveals ---------- */
  // Generic blocks: fade up as they enter
  const genericReveals = reveals.filter((el) => !el.matches('.card, .use-card'));
  ScrollTrigger.batch(genericReveals, {
    start: 'top 88%', once: true,
    onEnter: (batch) => gsap.to(batch, { autoAlpha: 1, y: 0, duration: 0.9, stagger: 0.1 })
  });

  // Product cards: staggered rise, row by row
  ScrollTrigger.batch($$('.card[data-reveal]'), {
    start: 'top 92%', once: true, interval: 0.12,
    onEnter: (batch) => gsap.to(batch, { autoAlpha: 1, y: 0, duration: 0.85, stagger: 0.09 })
  });

  // Shop-by-use tiles: soft mask opening + image settle
  $$('.use-card[data-reveal]').forEach((card, i) => {
    const img = $('img', card);
    gsap.set(card, { autoAlpha: 1, y: 0, clipPath: 'inset(10% 6% 10% 6% round 22px)' });
    if (img) gsap.set(img, { scale: 1.18 });
    ScrollTrigger.create({
      trigger: card, start: 'top 90%', once: true,
      onEnter: () => {
        gsap.to(card, { clipPath: 'inset(0% 0% 0% 0% round 22px)', duration: 1.2, ease: 'expo.out', delay: (i % 3) * 0.08 });
        if (img) gsap.to(img, { scale: 1, duration: 1.6, ease: 'expo.out', delay: (i % 3) * 0.08, clearProps: 'transform' });
      }
    });
  });

  // Child staggers inside revealed blocks
  const stagger = (parentSel, childSel, vars = {}) => {
    $$(parentSel).forEach((parent) => {
      const kids = $$(childSel, parent);
      if (!kids.length) return;
      gsap.set(kids, { autoAlpha: 0, y: 18, ...vars.from });
      ScrollTrigger.create({
        trigger: parent, start: 'top 85%', once: true,
        onEnter: () => gsap.to(kids, { autoAlpha: 1, y: 0, x: 0, duration: 0.7, stagger: 0.09, delay: 0.15, ...vars.to })
      });
    });
  };
  stagger('.steps', '.step');
  stagger('.wholesale__list', 'li', { from: { x: -12, y: 0 } });
  stagger('.segments', 'li', { from: { y: 10 }, to: { stagger: 0.05 } });
  stagger('.faq', 'details');
  stagger('.courier__facts', 'li', { from: { x: -10, y: 0 } });
  stagger('.size-guide__list', 'li', { from: { x: 16, y: 0 } });
  stagger('.tiers', '.tier');
  stagger('.footer__top', ':scope > *', { to: { stagger: 0.08 } });

  // Step numbers count up 01 → 04 feel: slide in from the left
  $$('.step__num').forEach((n) => gsap.from(n, { xPercent: -30, autoAlpha: 0, duration: 0.8, scrollTrigger: { trigger: n, start: 'top 88%', once: true } }));

  // Size guide: towels grow up from the floor, then their labels appear
  const canvas = $('[data-size-canvas]');
  if (canvas) {
    const rects = $$('.towel rect', canvas);
    const lines = $$('.towel line', canvas);
    const labels = $$('.towel text', canvas);
    gsap.set(rects, { scaleY: 0, transformOrigin: '50% 100%' });
    gsap.set([...lines, ...labels], { autoAlpha: 0 });
    ScrollTrigger.create({
      trigger: canvas, start: 'top 80%', once: true,
      onEnter: () => {
        gsap.timeline()
          .to(rects, { scaleY: 1, duration: 1.1, stagger: 0.14, ease: 'power3.inOut' })
          .to(lines, { autoAlpha: 1, duration: 0.4, stagger: 0.05 }, '-=0.4')
          .to(labels, { autoAlpha: 1, duration: 0.5, stagger: 0.06 }, '-=0.5');
      }
    });
  }

  // Subtle parallax on the hero stack and the wholesale photo
  if (hero && window.matchMedia('(min-width: 900px)').matches) {
    stackImgs.forEach((s, i) => gsap.to(s, {
      y: [-28, -60, -14][i] || -20, ease: 'none',
      scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: 0.6 }
    }));
  }
  $$('.wholesale__media img').forEach((img) => gsap.fromTo(img, { yPercent: -6, scale: 1.12 }, {
    yPercent: 6, ease: 'none', scrollTrigger: { trigger: img.parentElement, start: 'top bottom', end: 'bottom top', scrub: 0.6 }
  }));

  // Product page gallery thumbs drift in
  const thumbs = $$('.gallery__thumb');
  if (thumbs.length) gsap.from(thumbs, { autoAlpha: 0, x: -10, duration: 0.6, stagger: 0.04, delay: 0.3 });

  /* ---------- 4. Smooth accordions (FAQ + product details) ---------- */
  $$('.faq details, .accordion details').forEach((d) => {
    const summary = $('summary', d);
    const body = summary && summary.nextElementSibling;
    if (!body) return;
    summary.addEventListener('click', (e) => {
      e.preventDefault();
      if (d.dataset.animating) return;
      d.dataset.animating = '1';
      if (!d.open) {
        d.open = true;
        gsap.fromTo(body, { height: 0, autoAlpha: 0 }, { height: 'auto', autoAlpha: 1, duration: 0.45, ease: 'power2.out', onComplete: () => { gsap.set(body, { clearProps: 'height' }); delete d.dataset.animating; ScrollTrigger.refresh(); } });
      } else {
        gsap.to(body, { height: 0, autoAlpha: 0, duration: 0.35, ease: 'power2.in', onComplete: () => { d.open = false; gsap.set(body, { clearProps: 'all' }); delete d.dataset.animating; ScrollTrigger.refresh(); } });
      }
    });
  });

  /* ---------- 5. Cart drawer: lines slide in when it opens ---------- */
  document.addEventListener('kh:cart-open', () => {
    const lines = $$('[data-cart-drawer] .line');
    gsap.fromTo(lines, { autoAlpha: 0, x: 24 }, { autoAlpha: 1, x: 0, duration: 0.5, stagger: 0.06, delay: 0.15, clearProps: 'transform' });
    const foot = $('[data-cart-drawer] .drawer__foot');
    if (foot) gsap.fromTo(foot, { autoAlpha: 0, y: 16 }, { autoAlpha: 1, y: 0, duration: 0.5, delay: 0.3 });
  });

  /* ---------- 6. Keep triggers accurate as fonts and images arrive ---------- */
  const refresh = () => ScrollTrigger.refresh();
  window.addEventListener('load', refresh, { once: true });
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(refresh);

  // Safety net: anything still hidden after 4s (e.g. a trigger that never fired) is shown.
  setTimeout(() => {
    $$('[data-reveal]').forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && getComputedStyle(el).opacity === '0') gsap.to(el, { autoAlpha: 1, y: 0, duration: 0.4 });
    });
  }, 4000);
})();
